import React from 'react';
import { Box, Container, Stack, Typography, alpha, useTheme } from '@mui/material';
import RtaDexLogo from '@/components/RtaDexLogo';

const navigation = [
  { label: '概览', href: '/#positioning' },
  { label: '智能合约撮合', href: '/#capabilities' },
  { label: 'RTA 闭环', href: '/#rta-lifecycle' },
  { label: '技术架构', href: '/#architecture' },
  { label: '智能节点', href: '/#agents' },
  { label: '治理与审计', href: '/#governance' },
  { label: '撮合入口', href: '/entry' },
] as const;

const externalLinkProps = {
  target: '_blank',
  rel: 'noopener noreferrer',
} as const;

const CompanyFooter: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const mutedColor = isDark ? '#94A3B8' : '#5F6B7A';
  const subtleColor = isDark ? '#64748B' : '#7B8794';
  const linkSx = {
    color: mutedColor,
    textDecoration: 'none',
    transition: 'color 0.2s ease',
    '&:hover': { color: theme.palette.primary.main },
  };

  return (
    <Box
      component="footer"
      data-testid="company-footer"
      sx={{
        borderTop: `1px solid ${theme.palette.divider}`,
        bgcolor: isDark ? '#020617' : '#F5F7F9',
        color: 'text.primary',
      }}
    >
      <Container maxWidth={false} sx={{ maxWidth: 1200, px: { xs: 3, md: 4 }, py: { xs: 7, md: 8 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: '1.6fr 1fr 1.1fr 1fr' },
            gap: { xs: 5, md: 6 },
          }}
        >
          <Box>
            <Box component="a" href="/" aria-label="RtaDex 首页" sx={{ display: 'inline-flex', textDecoration: 'none' }}>
              <RtaDexLogo />
            </Box>
            <Typography sx={{ mt: 3, color: mutedColor, fontSize: 13, lineHeight: 1.8 }}>
              环球数科股份有限公司，2001 年成立于深圳。
            </Typography>
            <Typography sx={{ mt: 1, color: mutedColor, fontSize: 13, lineHeight: 1.8 }}>
              使命：科技创新赋能美好生活。
            </Typography>
            <Box sx={{ mt: 3 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>生态站点</Typography>
              <Typography
                component="a"
                href="https://tibcos.com/"
                {...externalLinkProps}
                sx={{ ...linkSx, display: 'inline-flex', flexDirection: 'column', mt: 1.5, fontSize: 13 }}
              >
                <Box component="span">TIBCOS 智旅链 ↗</Box>
                <Box component="span" sx={{ mt: 0.25, color: subtleColor, fontFamily: 'monospace', fontSize: 11 }}>
                  tibcos.com
                </Box>
              </Typography>
            </Box>
            <Typography sx={{ mt: 3, color: subtleColor, fontSize: 12, lineHeight: 1.7 }}>
              许可型智能合约撮合平台 · 面向文旅权益数字化、机构协作与全流程审计
            </Typography>
          </Box>

          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>导航</Typography>
            <Stack component="nav" aria-label="页脚导航" spacing={1.25} sx={{ mt: 2.5, alignItems: 'flex-start' }}>
              {navigation.map((item) => (
                <Typography key={item.href} component="a" href={item.href} sx={{ ...linkSx, fontSize: 13 }}>
                  {item.label}
                </Typography>
              ))}
            </Stack>
          </Box>

          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>联系我们</Typography>
            <Stack spacing={1.25} sx={{ mt: 2.5, color: mutedColor }}>
              <Typography sx={{ fontSize: 13, lineHeight: 1.7 }}>
                深圳湾科技生态园10栋B座<br />17层01-03号
              </Typography>
              <Typography component="a" href="tel:075525138888" sx={{ ...linkSx, fontSize: 13 }}>
                电话：0755-25138888
              </Typography>
              <Typography component="a" href="mailto:zb@hqshuke.com" sx={{ ...linkSx, fontSize: 13 }}>
                邮箱：zb@hqshuke.com
              </Typography>
            </Stack>
          </Box>

          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>成员企业</Typography>
            <Typography
              component="a"
              href="http://www.dataorigin.com.hk/index.htm"
              {...externalLinkProps}
              sx={{ ...linkSx, display: 'inline-flex', flexDirection: 'column', mt: 2.5, fontSize: 13 }}
            >
              <Box component="span">數字起源</Box>
              <Box component="span" sx={{ mt: 0.25, color: subtleColor, fontFamily: 'monospace', fontSize: 11 }}>
                dataorigin.com.hk
              </Box>
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            mt: 7,
            pt: 3,
            borderTop: `1px solid ${alpha(theme.palette.text.primary, isDark ? 0.12 : 0.1)}`,
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            alignItems: { xs: 'flex-start', lg: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            color: subtleColor,
          }}
        >
          <Typography sx={{ fontSize: 12, lineHeight: 1.7 }}>
            © 环球数科股份有限公司 {new Date().getFullYear()} 保留一切权利 ·{' '}
            <Box
              component="a"
              href="https://beian.miit.gov.cn/"
              {...externalLinkProps}
              data-testid="company-footer-icp"
              sx={{ ...linkSx, color: 'inherit' }}
            >
              粤ICP备09156180号
            </Box>
          </Typography>
          <Stack direction="row" spacing={{ xs: 2, sm: 2.5 }} useFlexGap flexWrap="wrap">
            <Typography component="a" href="https://www.hqshuke.com/about.html#contact-about" {...externalLinkProps} sx={{ ...linkSx, fontSize: 12 }}>
              联系我们
            </Typography>
            <Typography component="a" href="https://www.hqshuke.com/privacy-policy.html" {...externalLinkProps} data-testid="company-footer-privacy" sx={{ ...linkSx, fontSize: 12 }}>
              隐私政策
            </Typography>
            <Typography component="a" href="https://www.hqshuke.com/terms-of-use.html" {...externalLinkProps} data-testid="company-footer-terms" sx={{ ...linkSx, fontSize: 12 }}>
              使用条款
            </Typography>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default CompanyFooter;
